import Logger from '@sm360/logger-sentry';
import { isEmpty, isString } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';

import { ConfigsService, LocaleService } from '@sm360/widget-commons-core-services';
import { InputAutoComplete } from '@sm360/widget-commons-forms';
import { ShiftDigitalService } from '@sm360/widget-commons-general';
import { Quote } from '@sm360/widget-commons-vehicle';

import ShowroomCBBService from 'trade-in-360/services/ShowroomCBBService/ShowroomCBBService.js';
import NewTradeInContext from '../../NewTradeInContext';

class CBBAutoComplete extends Component {
    constructor(props) {
        super(props);

        this.state = {
            autoCompleteValues: [],
            isLoading: false,
            startedInput: false,
        };

        this.handleReset = this.handleReset.bind(this);
        this.handleSelectedOption = this.handleSelectedOption.bind(this);
        this.initializeAutoCompleteValues = this.initializeAutoCompleteValues.bind(this);
        this.updateAutocompleteValues = this.updateAutocompleteValues.bind(this);

        this.logger = new Logger(this.constructor.displayName, ConfigsService.getLoggerContext());

        this.inputAutoCompleteRef = null;
    }

    componentDidMount() {
        this.initializeAutoCompleteValues(this.props);
    }

    componentWillReceiveProps(newProps) {
        this.initializeAutoCompleteValues(newProps);
    }

    static contextType = NewTradeInContext;

    handleReset() {
        this.setState({
            autoCompleteValues: [],
        });
        this.props.onValueSelected('', null, null);
    }

    handleSelectedOption(inputValue, handler) {
        const selectedValue = this.state.autoCompleteValues.find((item) => item.value === inputValue);
        ShiftDigitalService.setDealerMake(this.props.config?.dealerMake);
        ShiftDigitalService.createTradeInVehicleEvent(selectedValue?.year, selectedValue?.make, selectedValue?.model);
        this.props.onValueSelected(inputValue, selectedValue, handler);
    }

    initializeAutoCompleteValues(props) {
        if (!this.state.autoCompleteValues.length && props.value !== '') {
            this.updateAutocompleteValues(props.value);
        }
    }

    updateAutocompleteValues(value) {
        if (!this.state.startedInput) {
            ShiftDigitalService.setDealerMake(this.props.config?.dealerMake);
            ShiftDigitalService.createTradeInStartEvent();
            ShiftDigitalService.updateDrTradeinStart(this.props.quote?.id, this.props.quote?.vehicle);
        }
        this.setState({
            isLoading: true,
            startedInput: true,
        });
        if (!isString(value) || isEmpty(value)) {
            this.logger.debug(`Value must be a non-empty string but got ${value}`);
            return;
        }

        const tradeVehicleQuery = {
            query: value,
        };

        ShowroomCBBService.searchCatalogVehicles(tradeVehicleQuery, false, 10, this.props.config)
            .then((results) => {
                this.setState({
                    autoCompleteValues: results.map((item, i) => ({
                        id: i,
                        name: item.value,
                        value: item.value,
                        make: item.make,
                        model: item.model,
                        year: item.year,
                        style: item.style,
                        trim: item.trim,
                        exteriorColors: item.exteriorColors,
                        interiorColors: item.interiorColors,
                        options: item.options,
                        tradeVehicleToken: item.tradeVehicleToken,
                        vin: item.vin,
                    })),
                    isLoading: false,
                });
            })
            .catch((e) => {
                this.logger.error(
                    'CBBAutoComplete#updateAutocompleteValues error calling ShowroomCBBService.searchCatalogVehicles',
                    e,
                    value
                );
                this.setState({
                    autoCompleteValues: [],
                    isLoading: false,
                });
            });
    }

    validateInput() {
        return this.inputAutoCompleteRef ? this.inputAutoCompleteRef.validateInput() : Promise.resolve(false);
    }

    render() {
        return (
            <InputAutoComplete
                errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.DEFAULT')}
                isLoading={this.state.isLoading}
                iconClassName="icon-review"
                items={this.state.autoCompleteValues}
                name="vehicleInformations"
                autoCompleteLabel={LocaleService.t('TRADE_IN.AUTOCOMPLETE_LABEL')}
                cantFindVehicleLabel={LocaleService.t('TRADE_IN.CANT_FIND_VEHICLE')}
                instantEstimateLabel={LocaleService.t('TRADE_IN.INSTANT_ESTIMATE_LABEL')}
                refreshItems={this.updateAutocompleteValues}
                suggestionsToDisplay={10}
                value={this.props.value}
                onResetValue={this.handleReset}
                onSelectedOption={this.handleSelectedOption}
                dataTestPrefix={this.props.dataTestPrefix}
                parentClassName={this.props.parentClassName}
                ref={(element) => {
                    this.inputAutoCompleteRef = element;
                }}
                isRequired
                displayOnWrapper={this.props.displayOnWrapper}
                placeholder={this.props.placeholder}
                isBarWrapper={this.props.isBarWrapper}
                vehicleInventoryId={this.props.config?.vehicleInventoryId}
            />
        );
    }
}

CBBAutoComplete.defaultProps = {
    value: '',
    dataTestPrefix: '',
    parentClassName: '',
    displayOnWrapper: '',
    placeholder: '',
    config: {},
    isBarWrapper: false,
    quote: new Quote.QuoteModel(),
};

CBBAutoComplete.propTypes = {
    onValueSelected: PropTypes.func.isRequired,
    value: PropTypes.string,
    dataTestPrefix: PropTypes.string,
    parentClassName: PropTypes.string,
    displayOnWrapper: PropTypes.bool,
    placeholder: PropTypes.string,
    config: PropTypes.object,
    isBarWrapper: PropTypes.bool,
    quote: PropTypes.instanceOf(Quote.QuoteModel),
};

CBBAutoComplete.displayName = 'CBBAutoComplete';

export default CBBAutoComplete;
